import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, Transition as _Transition, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "wobbleProtector" };
const _hoisted_2 = { key: 0 };
const _hoisted_3 = {
    key: 1,
    class: "moves-title-row"
};
const _hoisted_4 = { class: "wobbleProtector" };
const _hoisted_5 = { key: 2 };
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useApi, getHttpInstance } from '@/composables/useApi';
import { useCustomer } from '@/composables/useCustomer';
import { useProducts } from '@/composables/useProducts';
import { usePolling } from '@/composables/usePolling';
import { useBanking } from '@/composables/useBanking';
import { useVouchers } from '@/composables/useVouchers';
import { useMoves } from '@/composables/useMoves';
import { useContent } from '@/composables/useContent';
import { useQuiz } from '@/composables/useQuiz';
import { version } from '../../package.json';
import HeaderBlock from './elements/HeaderBlock.vue';
import AwaitBankAccountPanel from './elements/AwaitBankAccountPanel.vue';
import SwipeCarousel from './elements/SwipeCarousel.vue';
import MovePanel from './elements/MovePanel.vue';
import LinkAccountsShoutPanel from './elements/LinkAccountsShoutPanel.vue';
import ReLinkAccountsShoutPanel from './elements/ReLinkAccountsShoutPanel.vue';
import SetPayCycleShoutPanel from './elements/SetPayCycleShoutPanel.vue';
import SpendingSummaryCard from './elements/SpendingSummaryCard.vue';
import { getSpendingAnalysis, getTransactionClassificationTree, buildSpendingOverviewBreakdown } from '@/composables/useSpendingAnalysis';
import { formatResponseRange } from '@/utils/helpers';
import { snapToSpendingCycle } from './elements/spendingAnalysisFilterState';
import RecentTransactionsInfoPanel from './elements/RecentTransactionsInfoPanel.vue';
import AccountsWidget from './elements/AccountsWidget.vue';
import NewsShoutPanel from './elements/NewsShoutPanel.vue';
import ScrollThumbBlock from './elements/ScrollThumbBlock.vue';
import SmoothScrollContainer from './elements/SmoothScrollContainer.vue';
import MovesWidget from './elements/MovesWidget.vue';
import InfoPanel from './elements/InfoPanel.vue';
import QuizCard from './elements/QuizCard.vue';
import QuizDrawerRulesPanel from './elements/QuizDrawerRulesPanel.vue';
import QuizDrawerPlayPanel from './elements/QuizDrawerPlayPanel.vue';
import QuizDrawerTermsPanel from './elements/QuizDrawerTermsPanel.vue';
import SpendingPotButton from './elements/SpendingPotButton.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
import AiChatCard from './elements/AiChatCard.vue';
import VouchersOverviewCard from './VouchersOverviewCard.vue';
import quizTestData from '@/quiztest.json';
export default /*@__PURE__*/ _defineComponent({
    __name: 'HomePage',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        const state = useAppState();
        useAuth();
        const api = useApi();
        const { getCustomer } = useCustomer();
        const { getBankingInfo, getOpenBankingLinkUrl } = useBanking();
        const { getAvailableVouchers, getRecommendedVouchers, getDiscountsStatement } = useVouchers();
        const { getPendingMoves } = useMoves();
        const { getLatestFinancialFitness } = useContent();
        const { getProducts } = useProducts();
        const { poll, getPollRemaining } = usePolling('banking');
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        const quizRulesDrawer = ref(null);
        const quizPlayDrawer = ref(null);
        const quizTermsDrawer = ref(null);
        const quizTestMode = ref(route.query.quiztest === 'true');
        const quizTestQuestionNumber = parseInt(route.query.q) || 1;
        const { answerResponse, activeQuestionAnswered, openToStats, shouldShowQuiz, loadQuizData, handleRulesClick, handleAnswerClick, handleShowStatsClick, handleHome, handleSubmitAnswer, handleTermsClick, handleNotificationsChange, handleQuizDrawerOpened, handleQuizDrawerClosed, } = useQuiz({
            quizTestMode: quizTestMode.value,
            quizTestData: quizTestMode.value ? quizTestData : null,
            quizTestQuestionNumber,
            refs: { quizRulesDrawer, quizPlayDrawer, quizTermsDrawer },
            handleAnswerClick: () => {
                handleAnswerClick();
            },
            cookies,
        });
        const relinkNeeded = ref(false);
        const featuredVouchers = ref([]);
        const recommendedVouchers = ref([]);
        const featuredVouchersReady = ref(false);
        const recommendedVouchersReady = ref(false);
        const adding = ref(false);
        const pendingShoutMoves = ref(null);
        const discountsStatement = ref(null);
        const potentialSavings12Months = ref(0);
        const pollingComplete = ref(!localStorage.getItem('poll_remaining_banking'));
        const transactionAccounts = computed(() => {
            const ret = [];
            if (state.bankInfo == null) {
                return ret;
            }
            state.bankInfo.accounts.forEach((a) => {
                if (a.accountType.prettyName.toLowerCase() == "current") {
                    ret.push(a);
                }
            });
            return ret;
        });
        const creditCards = computed(() => {
            const ret = [];
            if (state.bankInfo == null) {
                return ret;
            }
            state.bankInfo.accounts.forEach((a) => {
                if (a.accountType.prettyName.toLowerCase() == "credit") {
                    ret.push(a);
                }
            });
            return ret;
        });
        const v2SummaryRaw = ref(null);
        const v2ClassificationTabs = ref([]);
        const spendingPeriodLabel = computed(() => {
            const r = v2SummaryRaw.value;
            if (r?.fromDate && r?.toDate)
                return formatResponseRange(r.fromDate, r.toDate);
            return 'Spending Analysis';
        });
        const homeSpendingSummary = computed(() => {
            const r = v2SummaryRaw.value;
            if (!r)
                return null;
            const totalIn = r.totalIn ?? 0;
            const totalOut = r.totalOut ?? 0;
            const breakdown = buildSpendingOverviewBreakdown(r.parentCategories, (name) => v2ClassificationTabs.value.find(t => t.label === name)?.color ?? null);
            return {
                rangeLabel: '',
                net: r.net ?? (totalIn - totalOut),
                totalIn,
                totalOut,
                breakdown,
            };
        });
        function fetchHomeV2Summary() {
            getTransactionClassificationTree((tree) => {
                if (tree?.parents) {
                    v2ClassificationTabs.value = tree.parents.map((p) => ({
                        label: p.name,
                        color: p.color ?? p.parent?.color ?? null,
                    }));
                }
            });
            getSpendingAnalysis(null, null, { snapToSpendingCycle: snapToSpendingCycle.value }, (result) => {
                if (result)
                    v2SummaryRaw.value = result;
            });
        }
        const showSpendingAnalysis = computed(() => {
            return state.bankInfo && state.bankInfo.accounts.length > 0 && state.bankInfo.spendingCycle.id !== "00000000-0000-0000-0000-000000000000";
        });
        const showSetupSpendingCycle = computed(() => {
            return state.bankInfo && state.bankInfo.accounts.length > 0 && state.bankInfo.spendingCycle.id == "00000000-0000-0000-0000-000000000000";
        });
        const savedToSpendBalance = computed(() => {
            return discountsStatement.value?.savedToSpendBalance || 0;
        });
        const voucherStateOverride = computed(() => {
            const v = parseInt(route.query.voucherState, 10);
            if (isNaN(v) || v < 1 || v > 4)
                return null;
            return { hasBankAccount: v === 2 || v === 4, hasVouchers: v === 3 || v === 4 };
        });
        const hasBankAccount = computed(() => {
            if (voucherStateOverride.value)
                return voucherStateOverride.value.hasBankAccount;
            return !!(state.bankInfo && state.bankInfo.accounts.length > 0);
        });
        const hasVouchers = computed(() => {
            if (voucherStateOverride.value)
                return voucherStateOverride.value.hasVouchers;
            return !!(discountsStatement.value?.allTimeSavings > 0);
        });
        const showNewVoucherCard = computed(() => {
            return localStorage.getItem('showNewVoucherCard') === 'true';
        });
        const brandItems = computed(() => {
            if (recommendedVouchers.value.length > 0)
                return recommendedVouchers.value;
            if (featuredVouchers.value.length > 0)
                return featuredVouchers.value;
            if (!state.availableVouchers)
                return [];
            return state.availableVouchers
                .filter((v) => v.merchant?.logoUrl)
                .slice(0, 8)
                .map((v) => ({ img: v.merchant.logoUrl, url: '/#/vouchers/amount/' + v.id }));
        });
        function getAppConfig(onSuccess = null) {
            const orig = getHttpInstance().defaults.headers.common;
            getHttpInstance().defaults.headers.common = {};
            axios.get("https://s3.eu-west-2.amazonaws.com/config.moneyguided.com/" + state.configUri + "_config.json")
                .then((result) => {
                state.config = result.data;
                // Version assertion
                if (state.config && state.config.vueAppVersion != version) {
                    if (!cookies?.get('block_version_reload')) {
                        cookies?.set('block_version_reload', null, '5min');
                        window.location.reload();
                    }
                }
                if (onSuccess)
                    onSuccess();
            })
                .catch(() => { });
            getHttpInstance().defaults.headers.common = orig;
        }
        function getBankInfo() {
            getBankingInfo(() => {
                fetchHomeV2Summary();
                const pollRemaining = getPollRemaining();
                if (pollRemaining) {
                    pollingComplete.value = false;
                    // what to poll for:
                    const initialCount = state.bankInfo.untaggedTransactionsCurrentCycleCount;
                    poll({
                        remaining: pollRemaining ?? 5,
                        interval: 2000,
                        fetch: (onSuccess) => getBankingInfo(onSuccess, true),
                        onPoll: () => { },
                        shouldStop: () => state.bankInfo.untaggedTransactionsCurrentCycleCount !== initialCount || state.bankInfo.untaggedTransactionsCurrentCycleCount === 0,
                        onFinish: () => {
                            pollingComplete.value = true;
                        },
                    });
                }
                state.bankInfo.accounts.forEach((a) => {
                    if (a.consentExpired) {
                        relinkNeeded.value = true;
                    }
                });
                if (cookies?.get('waitBank')) {
                    adding.value = true;
                }
            });
        }
        function onLoad() {
            if (quizTestMode.value) {
                loadQuizData();
            }
            api.clearExpiredCaches();
            getAppConfig();
            getCustomer(() => {
                getLatestFinancialFitness();
                getDiscountsStatement((result) => {
                    discountsStatement.value = result;
                });
                getBankInfo();
                getPendingMoves((moves) => {
                    pendingShoutMoves.value = moves.filter((move) => move.move.shout == true);
                    state.pendingMoves = moves.filter((move) => move.move.shout == false);
                }, true);
                getProducts(state.customer.employerID);
                getAvailableVouchers(() => {
                    featuredVouchers.value = [];
                    state.availableVouchers.forEach((v) => {
                        if (v.featured) {
                            featuredVouchers.value.push({ img: v.merchant.logoUrl, url: '/#/vouchers/amount/' + v.id });
                        }
                    });
                    featuredVouchersReady.value = true;
                    featuredVouchers.value = featuredVouchers.value.sort(() => Math.random() - 0.5);
                    getRecommendedVouchers(-365, (r) => {
                        potentialSavings12Months.value = r.reduce((sum, v) => sum + (v.saving || 0), 0);
                        r.forEach((v) => {
                            recommendedVouchers.value.push({ img: v.merchant.logoUrl, url: '/#/vouchers/amount/' + v.id });
                        });
                        recommendedVouchersReady.value = true;
                    });
                });
                getProducts();
                if (!quizTestMode.value) {
                    loadQuizData();
                }
            });
        }
        function navigateToSpendingAnalysis() {
            router.push('/SpendingAnalysis');
        }
        function handleConnectAccounts() {
            if (!state.customer)
                return;
            adding.value = true;
            const navTo = getOpenBankingLinkUrl(state.customer.id);
            window.open(navTo, '_blank');
        }
        onMounted(() => {
            onLoad();
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_row = _resolveComponent("v-row");
            const _component_v_carousel_item = _resolveComponent("v-carousel-item");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: _unref(state).timeOfDay + (_unref(state).customer ? (', ' + _unref(state).customer.person.firstName) : '')
                }, null, 8, ["title"]),
                _createVNode(AiChatBanner),
                (!_unref(state).bankInfo)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 0,
                        type: "card",
                        class: "carousel-skeleton"
                    }))
                    : (adding.value)
                        ? (_openBlock(), _createBlock(AwaitBankAccountPanel, {
                            key: 1,
                            origCount: _unref(state).bankInfo.accounts.length,
                            onAccountAdded: _cache[0] || (_cache[0] = ($event) => { adding.value = false; getBankInfo(); })
                        }, null, 8, ["origCount"]))
                        : (_openBlock(), _createBlock(SwipeCarousel, {
                            key: 2,
                            showArrows: false,
                            showButtons: true
                        }, {
                            default: _withCtx(() => [
                                (pendingShoutMoves.value && pendingShoutMoves.value.length > 0)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 0 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(MovePanel, {
                                                                lite: true,
                                                                move: pendingShoutMoves.value[0].move,
                                                                customerMove: pendingShoutMoves.value[0]
                                                            }, null, 8, ["move", "customerMove"])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length == 0 && !adding.value)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 1 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(LinkAccountsShoutPanel)
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                (relinkNeeded.value)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 2 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(ReLinkAccountsShoutPanel)
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                (showSetupSpendingCycle.value)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 3 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(SetPayCycleShoutPanel)
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                (showSpendingAnalysis.value && homeSpendingSummary.value)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 4 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createElementVNode("div", {
                                                                class: "homeSpendingSummaryWrap",
                                                                onClick: _cache[1] || (_cache[1] = ($event) => (navigateToSpendingAnalysis()))
                                                            }, [
                                                                _createVNode(SpendingSummaryCard, {
                                                                    compact: true,
                                                                    compactTitle: spendingPeriodLabel.value,
                                                                    data: homeSpendingSummary.value
                                                                }, null, 8, ["compactTitle", "data"])
                                                            ])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                (pollingComplete.value && _unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && _unref(state).bankInfo.recentTransactions && _unref(state).bankInfo.recentTransactions.length > 0)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 5 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(RecentTransactionsInfoPanel, {
                                                                today: _unref(state).today,
                                                                bankInfo: _unref(state).bankInfo
                                                            }, null, 8, ["today", "bankInfo"])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 6 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(NewsShoutPanel)
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0)
                                    ? (_openBlock(), _createBlock(_component_v_carousel_item, { key: 7 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_row, { class: "carouselPanel" }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(AccountsWidget, {
                                                                accounts: _unref(state).bankInfo.accounts,
                                                                formatter: _unref(state).formatter
                                                            }, null, 8, ["accounts", "formatter"])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true)
                            ]),
                            _: 1
                        })),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (_unref(activeQuestionAnswered) !== null &&
                            _unref(shouldShowQuiz) &&
                            _unref(state).quizzes && _unref(state).quizzes.length > 0 &&
                            _unref(state).quizPoints &&
                            _unref(state).quizAnswers !== null)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "mt-2 mb-2"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                            _createVNode(QuizCard, {
                                                quiz: _unref(state).quizzes[0],
                                                quizPoints: _unref(state).quizPoints,
                                                activeQuestionAnswered: _unref(activeQuestionAnswered),
                                                answers: _unref(state).quizAnswers,
                                                onRulesClick: _unref(handleRulesClick),
                                                onAnswerClick: _unref(handleAnswerClick),
                                                onShowStats: _unref(handleShowStatsClick)
                                            }, null, 8, ["quiz", "quizPoints", "activeQuestionAnswered", "answers", "onRulesClick", "onAnswerClick", "onShowStats"])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (showNewVoucherCard.value && (!featuredVouchersReady.value || !recommendedVouchersReady.value || !discountsStatement.value))
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 3,
                        type: "image, article, button",
                        class: "voucher-card-skeleton mt-4"
                    }))
                    : (showNewVoucherCard.value)
                        ? (_openBlock(), _createBlock(VouchersOverviewCard, {
                            key: 4,
                            hasBankAccount: hasBankAccount.value,
                            hasVouchers: hasVouchers.value,
                            allTimeSavings: discountsStatement.value?.allTimeSavings || 0,
                            savedToSpend: savedToSpendBalance.value,
                            savedLastMonth: discountsStatement.value?.savedLastMonth || 0,
                            potentialSavings: potentialSavings12Months.value > 0 ? _unref(state).formatter.format(potentialSavings12Months.value) : '',
                            brandItems: brandItems.value,
                            estimatedAnnualSavings: "£300",
                            onExplore: _cache[2] || (_cache[2] = ($event) => (_unref(router).push('/ShopVouchers'))),
                            onConnectAccounts: handleConnectAccounts,
                            class: "mt-4"
                        }, null, 8, ["hasBankAccount", "hasVouchers", "allTimeSavings", "savedToSpend", "savedLastMonth", "potentialSavings", "brandItems"]))
                        : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            (showNewVoucherCard.value && _unref(state).marketplace && _unref(state).marketplace.length > 0)
                                ? (_openBlock(), _createBlock(ScrollThumbBlock, {
                                    key: 0,
                                    class: "marketplace-full",
                                    href: "/#/Marketplace",
                                    title: "Marketplace",
                                    subtitle: "Explorer deals with our partners",
                                    thumbs: _unref(state).marketplace,
                                    rightimage: require('@/assets/arrow-right.png')
                                }, null, 8, ["thumbs", "rightimage"]))
                                : (!(featuredVouchersReady.value && recommendedVouchersReady.value))
                                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                                        key: 1,
                                        type: "image",
                                        class: "scroll-skeleton"
                                    }))
                                    : (_unref(state).marketplace && _unref(state).marketplace.length > 0)
                                        ? (_openBlock(), _createBlock(SmoothScrollContainer, { key: 2 }, {
                                            default: _withCtx(() => [
                                                (recommendedVouchers.value.length > 0)
                                                    ? (_openBlock(), _createBlock(ScrollThumbBlock, {
                                                        key: 0,
                                                        class: "smoothScrollPanel",
                                                        href: "/#/ShopVouchers?recommended=true",
                                                        title: "Discounts for you",
                                                        subtitle: "Based on your spending",
                                                        thumbs: recommendedVouchers.value,
                                                        rightimage: require('@/assets/arrow-right.png')
                                                    }, {
                                                        footer: _withCtx(() => [
                                                            _createVNode(SpendingPotButton, {
                                                                amount: savedToSpendBalance.value,
                                                                onClick: _cache[3] || (_cache[3] = ($event) => (_unref(router).push('/SavedToSpend'))),
                                                                class: "spending-pot-footer"
                                                            }, null, 8, ["amount"])
                                                        ]),
                                                        _: 1
                                                    }, 8, ["thumbs", "rightimage"]))
                                                    : (_openBlock(), _createBlock(ScrollThumbBlock, {
                                                        key: 1,
                                                        class: "smoothScrollPanel",
                                                        href: "/#/ShopVouchers?recommended=false",
                                                        title: "Featured Discounts",
                                                        subtitle: "Save money every day",
                                                        thumbs: featuredVouchers.value,
                                                        rightimage: require('@/assets/arrow-right.png')
                                                    }, {
                                                        footer: _withCtx(() => [
                                                            _createVNode(SpendingPotButton, {
                                                                amount: savedToSpendBalance.value,
                                                                onClick: _cache[4] || (_cache[4] = ($event) => (_unref(router).push('/SavedToSpend'))),
                                                                class: "spending-pot-footer"
                                                            }, null, 8, ["amount"])
                                                        ]),
                                                        _: 1
                                                    }, 8, ["thumbs", "rightimage"])),
                                                _createVNode(ScrollThumbBlock, {
                                                    class: "smoothScrollPanel",
                                                    href: "/#/Marketplace",
                                                    title: "Marketplace",
                                                    subtitle: "Explorer deals with our partners",
                                                    thumbs: _unref(state).marketplace,
                                                    rightimage: require('@/assets/arrow-right.png')
                                                }, null, 8, ["thumbs", "rightimage"])
                                            ]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 1
                }),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (_unref(state).financialFitness && _unref(state).financialFitness.length == 0)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "financialFitnessRow"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                            _createVNode(InfoPanel, {
                                                href: "/#/FinancialFitness",
                                                valueHref: "/#/FinancialFitnessHistory",
                                                icon: require('@/assets/financialFitnessIcon.png'),
                                                title: "My Financial Fitness",
                                                subtitle: "Let's calculate your baseline",
                                                valueTitle: "Score",
                                                value: "--"
                                            }, null, 8, ["icon"])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        _createElementVNode("div", null, [
                            (!_unref(state).pendingMoves)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                    _createVNode(_component_v_row, { class: "title-row" }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_col, { class: "title-col" }, {
                                                default: _withCtx(() => _cache[7] || (_cache[7] = [
                                                    _createTextVNode("Moves")
                                                ])),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }),
                                    _createVNode(_component_v_skeleton_loader, {
                                        type: "list-item-two-line, list-item-two-line",
                                        class: "moves-skeleton"
                                    })
                                ]))
                                : (_unref(state).pendingMoves.length > 0)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                        _createVNode(_component_v_row, { class: "title-row" }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_col, { class: "title-col" }, {
                                                    default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                        _createTextVNode("Moves")
                                                    ])),
                                                    _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                    class: "right",
                                                    style: { "padding-top": "20px" }
                                                }, {
                                                    default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                        _createElementVNode("a", { href: "/#/Moves" }, "See All", -1)
                                                    ])),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1
                                        }),
                                        _createVNode(_component_v_row, { class: "mt-0" }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_col, { class: "moves-widget-col" }, {
                                                    default: _withCtx(() => [
                                                        _createElementVNode("div", _hoisted_4, [
                                                            (_unref(state).pendingMoves)
                                                                ? (_openBlock(), _createBlock(MovesWidget, {
                                                                    key: 0,
                                                                    moves: _unref(state).pendingMoves,
                                                                    lite: true
                                                                }, null, 8, ["moves"]))
                                                                : _createCommentVNode("", true)
                                                        ])
                                                    ]),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1
                                        })
                                    ]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                        _createVNode(_component_v_row, { class: "title-row" }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_col, { class: "title-col" }, {
                                                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                        _createTextVNode("Moves")
                                                    ])),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1
                                        }),
                                        _createVNode(_component_v_row, null, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_col, null, {
                                                    default: _withCtx(() => [
                                                        _createVNode(InfoPanel, {
                                                            class: "shadowed",
                                                            icon: require('@/assets/bullet-tick.png'),
                                                            title: "All Moves Completed!",
                                                            subtitle: "You're up to date with all your moves..."
                                                        }, null, 8, ["icon"])
                                                    ]),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1
                                        })
                                    ]))
                        ])
                    ]),
                    _: 1
                }),
                (_unref(state).quizzes && _unref(state).quizzes.length > 0)
                    ? (_openBlock(), _createBlock(QuizDrawerRulesPanel, {
                        key: 5,
                        ref_key: "quizRulesDrawer",
                        ref: quizRulesDrawer,
                        openHeight: '95%',
                        quiz: _unref(state).quizzes[0],
                        customer: _unref(state).customer,
                        onTermsClick: _unref(handleTermsClick),
                        onNotificationsChange: _unref(handleNotificationsChange)
                    }, null, 8, ["quiz", "customer", "onTermsClick", "onNotificationsChange"]))
                    : _createCommentVNode("", true),
                (_unref(state).quizzes && _unref(state).quizzes.length > 0)
                    ? (_openBlock(), _createBlock(QuizDrawerPlayPanel, {
                        key: 6,
                        ref_key: "quizPlayDrawer",
                        ref: quizPlayDrawer,
                        openHeight: '100%',
                        quiz: _unref(state).quizzes[0],
                        customer: _unref(state).customer,
                        originalQuizPoints: _unref(state).quizPoints,
                        answerResponse: _unref(answerResponse),
                        openToStats: _unref(openToStats),
                        onTermsClick: _unref(handleTermsClick),
                        onBackToHome: _cache[5] || (_cache[5] = ($event) => (_unref(handleHome)())),
                        onSubmitAnswer: _cache[6] || (_cache[6] = ($event) => (_unref(handleSubmitAnswer)(_unref(state).quizzes[0], $event))),
                        onDrawerOpened: _unref(handleQuizDrawerOpened),
                        onDrawerClosed: _unref(handleQuizDrawerClosed),
                        onNotificationsChange: _unref(handleNotificationsChange)
                    }, null, 8, ["quiz", "customer", "originalQuizPoints", "answerResponse", "openToStats", "onTermsClick", "onDrawerOpened", "onDrawerClosed", "onNotificationsChange"]))
                    : _createCommentVNode("", true),
                (_unref(state).quizzes && _unref(state).quizzes.length > 0)
                    ? (_openBlock(), _createBlock(QuizDrawerTermsPanel, {
                        key: 7,
                        ref_key: "quizTermsDrawer",
                        ref: quizTermsDrawer,
                        termsUrl: _unref(state).quizzes[0].termsUrl
                    }, null, 8, ["termsUrl"]))
                    : _createCommentVNode("", true),
                _createVNode(AiChatCard, { class: "ai-chat-card-section" })
            ], 64));
        };
    }
});
